/**
 * Loan v3.5 Phase C — Customer Magic-Link Portal (V4.24.2)
 *
 * Public route: `/loan-portal/:token`
 * No login required — the JWT magic token in the URL is the credential.
 *
 * Flow (4 steps):
 *   1. GET  /api/public/v1/loan-portal/resume?token=  → hydrate state + prefill
 *   2. Section 1 (personal & employment + joint applicants)  → PUT /section-1
 *   3. Section 2 (income, loan prefs, tenure, rate, extras)  → PUT /section-2
 *   4. Section 4 (supporting documents)                      → PUT /section-4
 *   5. Consent (PDPA + Aicon + bank fan-out + outsource)     → POST /consent
 *   6. Result panel — risk band, recommended loan, top banks.
 *
 * Read-only rule: any field whose value is prefilled from the booking /
 * property / user record (advertised via `readonly_fields[]` in the resume
 * response) renders as a locked input. Empty prefill → user can edit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  CheckCircle2, AlertTriangle, ShieldCheck, Banknote, ArrowRight, ArrowLeft,
  Loader2, Home, Building2, Upload, FileText, Trash2, Plus, Lock, Save, LogOut,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/v1`;

const SECTION_LABELS = [
  '1 · Personal',
  '2 · Income & Loan',
  '3 · Documents',
  '4 · Consent',
];

export default function LoanPortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, caseData: null });
  const [step, setStep] = useState(0);
  const [section1, setSection1] = useState(emptySection1());
  const [section2, setSection2] = useState(emptySection2());
  const [section4, setSection4] = useState(emptySection4());
  const [consent, setConsent] = useState({
    typed_nric: '',
    agreed_pdpa: false,
    agreed_aicon_ctos: false,
    agreed_bank_fanout: false,
    agreed_outsource_fallback: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftClosedNotice, setDraftClosedNotice] = useState(false);

  const readonlySet = useMemo(
    () => new Set(state.caseData?.readonly_fields || []),
    [state.caseData]
  );
  const prefill = state.caseData?.prefill || {};
  const propSummary = state.caseData?.property_summary || {};
  // V4.24.27 — Dual-audience magic links. `/loan-portal/resume` returns:
  //   audience     ∈ 'customer' | 'agent'   (defaults to customer for legacy tokens)
  //   permissions  = { can_submit_consent, message_for_step_4, ... }
  // Frontend uses these to (a) show a header ribbon identifying whose
  // link is open, and (b) block Step 4 for agent tokens.
  const audience = state.caseData?.audience || 'customer';
  const permissions = state.caseData?.permissions || {};
  const isAgentLink = audience === 'agent';
  const canSubmitConsent = permissions.can_submit_consent !== false;

  // Load case on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/loan-portal/resume`, { params: { token } });
        const c = r.data?.data || {};
        setState({ loading: false, error: null, caseData: c });
        // Merge prefill into Section 1 so prefilled fields are locked but visible.
        const s1 = { ...emptySection1(), ...(c.section_1 || {}) };
        if (c.prefill?.full_name && !s1.full_name) s1.full_name = c.prefill.full_name;
        if (c.prefill?.nric && !s1.nric) s1.nric = String(c.prefill.nric).replace(/\D/g, '').slice(0, 12);
        // Normalise any pre-existing/dashed NRIC to the 12-digit stored form.
        if (s1.nric) s1.nric = String(s1.nric).replace(/\D/g, '').slice(0, 12);
        if (c.prefill?.email && !s1.email) s1.email = c.prefill.email;
        if (c.prefill?.phone && !s1.phone) s1.phone = c.prefill.phone;
        setSection1(s1);
        setSection2({ ...emptySection2(), ...(c.section_2 || {}) });
        setSection4({ ...emptySection4(), ...(c.section_4 || {}) });
        // V4.24.14 — Restore draft consent checkboxes if the customer saved
        // partial consent as a draft earlier.
        if (c.consent_draft && typeof c.consent_draft === 'object') {
          setConsent(prev => ({ ...prev, ...c.consent_draft }));
        }
        // Pick the right starting step
        if (c.status === 'profiled' || c.status === 'special_handling') {
          setStep(4); // Result view
        } else if (c.section_status?.section_4_done) {
          setStep(3);
        } else if (c.section_status?.section_2_done) {
          setStep(2);
        } else if (c.section_status?.section_1_done) {
          setStep(1);
        } else {
          setStep(0);
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.detail || 'Magic link invalid or expired.';
        setState({ loading: false, error: msg, caseData: null });
      }
    })();
  }, [token]);

  // ─────────── Step handlers ───────────
  const NRIC_MSG = 'NRIC must be 12 digits with no dashes or spaces.';
  const isNricValid = (v) => /^\d{12}$/.test(String(v || '').trim());

  const saveSection1 = async () => {
    // Frontend guard — mirrors the backend Section1Applicant validator.
    if (!isNricValid(section1.nric)) {
      toast.error(NRIC_MSG);
      return;
    }
    if ((section1.joint_applicants || []).some(j => !isNricValid(j.nric))) {
      toast.error(`Co-applicant ${NRIC_MSG}`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/loan-portal/section-1`, section1, { params: { token } });
      toast.success('Section 1 saved');
      setStep(1);
    } catch (e) {
      handleValidationError(e, 'Could not save Section 1');
    } finally { setSubmitting(false); }
  };

  const saveSection2 = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/loan-portal/section-2`, section2, { params: { token } });
      toast.success('Section 2 saved');
      setStep(2);
    } catch (e) {
      handleValidationError(e, 'Could not save Section 2');
    } finally { setSubmitting(false); }
  };

  const saveSection4 = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/loan-portal/section-4`, section4, { params: { token } });
      toast.success('Documents saved');
      setStep(3);
    } catch (e) {
      handleValidationError(e, 'Could not save documents');
    } finally { setSubmitting(false); }
  };

  const submitConsent = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/loan-portal/consent`, consent, { params: { token } });
      const c = r.data?.data || {};
      setState({ loading: false, error: null, caseData: c });
      toast.success('Loan profile completed!');
      setStep(4);
    } catch (e) {
      handleValidationError(e, 'Could not submit consent');
    } finally { setSubmitting(false); }
  };

  // V4.24.14 — Save-as-Draft. Persists whatever the customer has partially
  // filled without validating required fields or marking any section done.
  // Available on every step and via the header "Save & Close" button so the
  // customer can leave and resume later.
  const saveDraft = async (opts = {}) => {
    const { closeAfter = false } = opts;
    setSavingDraft(true);
    try {
      // Only send sections that have any user-entered data. The backend
      // merges into whatever is already stored.
      const body = {};
      if (section1 && Object.values(section1).some(v => v && String(v).length > 0)) body.section_1 = section1;
      if (section2 && Object.values(section2).some(v => v && (Array.isArray(v) ? v.length : String(v).length))) body.section_2 = section2;
      if (section4 && Object.values(section4).some(v => Array.isArray(v) && v.length > 0)) body.section_4 = section4;
      if (consent && Object.values(consent).some(v => v && String(v).length > 0)) body.consent = consent;

      await axios.post(`${API}/loan-portal/save-draft`, body, { params: { token } });
      toast.success('Draft saved. You can resume this later from your magic link.');

      if (closeAfter) {
        // Give the toast a moment, then either navigate back or show a "you can close this tab" hint.
        setTimeout(() => {
          setDraftClosedNotice(true);
        }, 600);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.detail || 'Could not save draft';
      toast.error(msg);
    } finally {
      setSavingDraft(false);
    }
  };

  const consentReady = useMemo(() => (
    !!consent.typed_nric &&
    consent.agreed_pdpa && consent.agreed_aicon_ctos &&
    consent.agreed_bank_fanout && consent.agreed_outsource_fallback &&
    // V4.25.21 — Aicon/CTOS retrieval requires a captured digital signature.
    // Guards the "sign then Clear then submit" gap: clearing wipes
    // aicon_signature_data_url, so submit is blocked until re-signed.
    !!consent.aicon_signature_data_url
  ), [consent]);

  // V4.24.6 — Section 4 requires NRIC + Payslips (AICon mandatory fields).
  const section4Ready = useMemo(() => (
    (section4.nric || []).length > 0 &&
    (section4.payslips || []).length > 0
  ), [section4]);

  // ─────────── Render ───────────
  if (state.loading) {
    return <FullPageStatus icon={<Loader2 className="w-10 h-10 animate-spin text-indigo-600" />} title="Loading your loan profile…" />;
  }

  if (state.error) {
    return <FullPageStatus
      icon={<AlertTriangle className="w-10 h-10 text-amber-600" />}
      title="Link Invalid or Expired"
      message={state.error}
      hint="Please contact your SAM Rumah agent to send a fresh link."
      data-testid="loan-portal-error"
    />;
  }

  const c = state.caseData;
  const isTerminal = (c?.status === 'profiled' || c?.status === 'special_handling') || step === 4;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-white" data-testid="loan-portal-page">
      <Toaster richColors position="top-center" />
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-violet-700 text-white">
        <div className="max-w-3xl mx-auto px-6 py-7 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
            <Home className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">SAM Rumah — Loan Profiling</h1>
            <p className="text-sm text-indigo-100 mt-0.5">
              {isTerminal ? 'Your profile has been submitted.' : 'Tell us about you so we can shortlist the best banks (~5 min).'}
            </p>
          </div>
          {/* V4.24.14 — Save & Close (leave for later) — only meaningful while
              the customer is still filling the questionnaire. */}
          {!isTerminal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveDraft({ closeAfter: true })}
              disabled={savingDraft || submitting}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
              data-testid="loan-portal-save-and-close"
            >
              {savingDraft
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                : <><LogOut className="w-4 h-4 mr-1" /> Save &amp; Close</>}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* V4.24.27 — Audience banner. Explains whose link the visitor is
            looking at so an agent doesn't try to submit consent themselves
            and a customer knows they're reviewing agent-filled data. */}
        {!isTerminal && (
          <div
            className={`mb-6 rounded-xl p-4 flex items-start gap-3 border ${
              isAgentLink
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-indigo-50 border-indigo-200 text-indigo-900'
            }`}
            data-testid={`loan-portal-audience-banner-${audience}`}
          >
            <ShieldCheck className={`w-5 h-5 mt-0.5 ${isAgentLink ? 'text-amber-600' : 'text-indigo-600'}`} />
            <div className="text-sm">
              <p className="font-semibold">
                {isAgentLink ? 'Agent Loan Questionnaire' : 'Customer Loan Questionnaire'}
              </p>
              <p className={`mt-0.5 ${isAgentLink ? 'text-amber-800/90' : 'text-indigo-800/90'}`}>
                {isAgentLink
                  ? 'You can help the customer complete Steps 1–3. Step 4 (Consent & Declaration) must be completed by the customer using their own link.'
                  : 'Please review Steps 1–3, then complete Step 4 (Consent & Declaration) to submit.'}
              </p>
            </div>
          </div>
        )}

        {/* V4.24.14 — After Save & Close, show a friendly notice so the
            customer knows they can safely leave and come back later. */}
        {draftClosedNotice && !isTerminal && (
          <div
            className="mb-6 border border-emerald-200 bg-emerald-50 rounded-xl p-4 flex items-start gap-3"
            data-testid="loan-portal-draft-saved-notice"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <p className="font-semibold">Draft saved.</p>
              <p className="mt-0.5 text-emerald-800/90">
                You can safely close this tab. Reopen the same magic link
                anytime to continue where you left off.
              </p>
            </div>
          </div>
        )}

        {/* Read-only Property Summary Card */}
        {!isTerminal && <PropertyCard summary={propSummary} />}

        {/* Stepper */}
        {!isTerminal && (
          <div className="mb-6 mt-6 flex items-center justify-between gap-2" data-testid="loan-portal-stepper">
            {SECTION_LABELS.map((label, i) => (
              <div key={i} className="flex-1 flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step ? 'bg-emerald-600 text-white' :
                  i === step ? 'bg-indigo-600 text-white ring-4 ring-indigo-200' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-xs hidden sm:inline ${i === step ? 'text-indigo-700 font-semibold' : 'text-gray-500'}`}>
                  {label}
                </span>
                {i < SECTION_LABELS.length - 1 && <div className="flex-1 h-0.5 bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        {/* Step content */}
        {!isTerminal && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
            {step === 0 && <Section1Form data={section1} setData={setSection1} readonlySet={readonlySet} />}
            {step === 1 && <Section2Form data={section2} setData={setSection2} />}
            {step === 2 && <Section4Form data={section4} setData={setSection4} token={token} />}
            {/* V4.24.27 — Step 4 is customer-only. When the caller is on the
                agent link, render an informational panel and hide the form
                entirely. The backend also 403s the consent POST, so this is
                belt-and-braces UX, not the only guard. */}
            {step === 3 && isAgentLink && (
              <div
                className="rounded-xl border border-amber-200 bg-amber-50 p-6 space-y-3"
                data-testid="loan-portal-agent-step4-block"
              >
                <div className="flex items-center gap-2 text-amber-800 font-semibold">
                  <ShieldCheck className="w-5 h-5" />
                  Consent & Declaration — Customer only
                </div>
                <p className="text-sm text-amber-900">
                  {permissions.message_for_step_4 ||
                    'The final Consent & Declaration must be completed by the customer. Share the customer link with them once Steps 1–3 are ready.'}
                </p>
                <p className="text-xs text-amber-800/80">
                  Only the customer can accept PDPA, AICon / CTOS, bank fan-out, and outsource-fallback consent. Submitting to AICon happens from the customer link.
                </p>
              </div>
            )}
            {step === 3 && !isAgentLink && (
              <ConsentForm data={consent} setData={setConsent}
                           nricFromSection1={c?.section_1?.nric || section1.nric}
                           customerSnapshot={{
                             // V4.24.82 — CTOS consent panel MUST use
                             // CUSTOMER data (not the agent). Prefer LIVE
                             // Section 1 form state (reflects what the
                             // user typed in this session even before
                             // save), then fall back to persisted
                             // case.section_1 / prefill.
                             name: section1.full_name || c?.section_1?.full_name
                                   || c?.prefill?.full_name || '',
                             ic_number: section1.nric || c?.section_1?.nric
                                        || c?.prefill?.nric || '',
                             mobile: section1.phone || c?.section_1?.phone
                                     || c?.prefill?.phone || '',
                             designation: section1.job_title || c?.section_1?.job_title
                                          || 'Individual',
                           }}
                           propertySummary={propSummary} />
            )}

            <div className="flex items-center justify-between pt-3 border-t gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => setStep(s => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
                data-testid="loan-portal-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                {/* V4.24.14 — Save as Draft available on every step. */}
                <Button
                  variant="outline"
                  onClick={() => saveDraft()}
                  disabled={savingDraft || submitting}
                  data-testid="loan-portal-save-draft"
                >
                  {savingDraft
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
                    : <><Save className="w-4 h-4 mr-1" /> Save as Draft</>}
                </Button>
                {step === 0 && (
                  <Button onClick={saveSection1} disabled={submitting} data-testid="loan-portal-save-s1">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                {step === 1 && (
                  <Button onClick={saveSection2} disabled={submitting} data-testid="loan-portal-save-s2">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                {step === 2 && (
                  <Button onClick={saveSection4} disabled={submitting || !section4Ready} data-testid="loan-portal-save-s4">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
                {step === 3 && !isAgentLink && (
                  <Button onClick={submitConsent} disabled={!consentReady || submitting}
                          data-testid="loan-portal-submit-consent">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</> : (
                      <>Submit Loan Profile <ShieldCheck className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {isTerminal && <ResultPanel caseData={c} />}

        <p className="text-center text-xs text-gray-400 mt-8">
          Sam Properties Realty Sdn Bhd · This link is private to you and expires automatically.
        </p>
      </div>
    </div>
  );
}

// ─────────── Property Summary Card (read-only) ───────────
function PropertyCard({ summary }) {
  if (!summary || !summary.project_name) return null;
  const price = (v) => (v == null ? '—' : `RM ${Number(v).toLocaleString()}`);
  return (
    <div className="bg-white border rounded-2xl p-5 shadow-sm" data-testid="loan-portal-property-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-indigo-700" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-800">{summary.project_name}</h2>
            {summary.loan_type_project && (
              <Badge variant="outline" className="text-xs">{summary.loan_type_project}</Badge>
            )}
            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs">
              <Lock className="w-3 h-3 mr-1" /> Locked
            </Badge>
          </div>
          {summary.developer && <p className="text-xs text-gray-500 mt-0.5">by {summary.developer}</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
            <KV label="Unit" value={summary.unit_number || '—'} />
            <KV label="Type" value={summary.property_type || '—'} />
            <KV label="Tenure" value={summary.tenure_type || '—'} />
            <KV label="State" value={summary.state || '—'} />
            <KV label="City" value={summary.city || '—'} />
            <KV label="Postcode" value={summary.postcode || '—'} />
            <KV label="Asking Price" value={price(summary.asking_price)} />
            <KV label="SPA Price" value={price(summary.spa_price)} />
            <KV label="Booking #" value={summary.booking_number || '—'} />
          </div>
          {summary.address_1 && (
            <p className="text-xs text-gray-500 mt-3"><span className="font-medium text-gray-600">Address:</span> {summary.address_1}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 font-medium">{value}</p>
    </div>
  );
}

// ─────────── Section 1 ───────────
function Section1Form({ data, setData, readonlySet }) {
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  const ro = (path) => readonlySet.has(`section_1.${path}`);

  const addJoint = () => setData(d => ({
    ...d,
    joint_applicants: [...(d.joint_applicants || []), emptyJoint()],
  }));
  const updJoint = (idx, k, v) => setData(d => ({
    ...d,
    joint_applicants: (d.joint_applicants || []).map((j, i) => i === idx ? { ...j, [k]: v } : j),
  }));
  const rmJoint = (idx) => setData(d => ({
    ...d,
    joint_applicants: (d.joint_applicants || []).filter((_, i) => i !== idx),
  }));

  return (
    <div className="space-y-4" data-testid="loan-portal-section-1">
      <h2 className="text-lg font-bold text-gray-800">Personal & Employment</h2>
      <p className="text-xs text-gray-500 -mt-2">Fields marked <Lock className="w-3 h-3 inline text-gray-500" /> are prefilled from your booking and cannot be edited.</p>

      {/* Prefilled contact block */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full Name" required locked={ro('full_name')}>
          <Input value={data.full_name || ''} onChange={e => upd('full_name', e.target.value)}
                 readOnly={ro('full_name')} className={ro('full_name') ? 'bg-gray-50' : ''}
                 data-testid="s1-full-name" />
        </Field>
        <Field label="NRIC / IC No." required locked={ro('nric')}>
          <Input value={data.nric}
                 onChange={e => upd('nric', e.target.value.replace(/\D/g, '').slice(0, 12))}
                 inputMode="numeric"
                 maxLength={12}
                 placeholder="880101145555"
                 readOnly={ro('nric')} className={ro('nric') ? 'bg-gray-50' : ''}
                 data-testid="s1-nric" />
          {data.nric && !/^\d{12}$/.test(String(data.nric).trim()) && (
            <p className="text-xs text-red-600 mt-1" data-testid="s1-nric-error">
              NRIC must be 12 digits with no dashes or spaces.
            </p>
          )}
        </Field>
        <Field label="Email" locked={ro('email')}>
          <Input value={data.email || ''} onChange={e => upd('email', e.target.value)}
                 readOnly={ro('email')} className={ro('email') ? 'bg-gray-50' : ''}
                 data-testid="s1-email" />
        </Field>
        <Field label="Phone" locked={ro('phone')}>
          <Input value={data.phone || ''} onChange={e => upd('phone', e.target.value)}
                 readOnly={ro('phone')} className={ro('phone') ? 'bg-gray-50' : ''}
                 data-testid="s1-phone" />
        </Field>
        <Field label="Date of Birth (DD/MM/YYYY)" required>
          <Input value={data.date_of_birth} onChange={e => upd('date_of_birth', e.target.value)}
                 placeholder="25/12/1990" data-testid="s1-dob" />
        </Field>
        <Field label="Residential Address">
          <Input value={data.residential_address || ''} onChange={e => upd('residential_address', e.target.value)}
                 placeholder="Home address" data-testid="s1-address" />
        </Field>
        <Field label="Nationality" required>
          <Select value={data.nationality} onValueChange={v => upd('nationality', v)}>
            <SelectTrigger data-testid="s1-nationality"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Malaysian">Malaysian</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Marital Status" required>
          <Select value={data.marital_status} onValueChange={v => upd('marital_status', v)}>
            <SelectTrigger data-testid="s1-marital"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="married">Married</SelectItem>
              <SelectItem value="divorced">Divorced</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Dependents">
          <Input type="number" value={data.dependents} onChange={e => upd('dependents', parseInt(e.target.value) || 0)}
                 data-testid="s1-dependents" />
        </Field>
        <Field label="Income Type" required>
          <Select value={data.income_type} onValueChange={v => upd('income_type', v)}>
            <SelectTrigger data-testid="s1-income-type"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="salaried_my">Salaried (Malaysia)</SelectItem>
              <SelectItem value="salaried_sg">Salaried (Singapore)</SelectItem>
              <SelectItem value="self_employed">Self-Employed</SelectItem>
              <SelectItem value="commission">Commission-Based</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Employer" required>
          <Input value={data.employer} onChange={e => upd('employer', e.target.value)} data-testid="s1-employer" />
        </Field>
        <Field label="Job Title">
          <Input value={data.job_title} onChange={e => upd('job_title', e.target.value)} data-testid="s1-job-title" />
        </Field>
        <Field label="Months in current job">
          <Input type="number" value={data.months_in_current_job}
                 onChange={e => upd('months_in_current_job', parseInt(e.target.value) || 0)}
                 data-testid="s1-months" />
        </Field>
        <Field label="On probation?">
          <Select value={data.on_probation ? 'yes' : 'no'} onValueChange={v => upd('on_probation', v === 'yes')}>
            <SelectTrigger data-testid="s1-probation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Joint Applicants */}
      <div className="border-t pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-800">Joint Applicants (optional)</h3>
            <p className="text-xs text-gray-500 mt-0.5">Add a spouse or family member applying with you.</p>
          </div>
          <Button size="sm" variant="outline" onClick={addJoint} data-testid="s1-add-joint">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {(data.joint_applicants || []).length === 0 && (
          <p className="text-xs text-gray-400 italic">No joint applicants added.</p>
        )}

        {(data.joint_applicants || []).map((j, idx) => (
          <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50" data-testid={`s1-joint-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Applicant #{idx + 2}</p>
              <Button size="sm" variant="ghost" onClick={() => rmJoint(idx)} data-testid={`s1-joint-${idx}-remove`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full Name" required>
                <Input value={j.full_name} onChange={e => updJoint(idx, 'full_name', e.target.value)} data-testid={`s1-joint-${idx}-name`} />
              </Field>
              <Field label="NRIC" required>
                <Input value={j.nric}
                       onChange={e => updJoint(idx, 'nric', e.target.value.replace(/\D/g, '').slice(0, 12))}
                       inputMode="numeric" maxLength={12} placeholder="900202146666"
                       data-testid={`s1-joint-${idx}-nric`} />
                {j.nric && !/^\d{12}$/.test(String(j.nric).trim()) && (
                  <p className="text-xs text-red-600 mt-1" data-testid={`s1-joint-${idx}-nric-error`}>
                    NRIC must be 12 digits with no dashes or spaces.
                  </p>
                )}
              </Field>
              <Field label="Relation">
                <Select value={j.relation} onValueChange={v => updJoint(idx, 'relation', v)}>
                  <SelectTrigger data-testid={`s1-joint-${idx}-relation`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spouse">Spouse</SelectItem>
                    <SelectItem value="sibling">Sibling</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Basic Salary (RM)">
                <Input type="number" value={j.basic_salary} onChange={e => updJoint(idx, 'basic_salary', parseFloat(e.target.value) || 0)} data-testid={`s1-joint-${idx}-salary`} />
              </Field>
              <Field label="Employer">
                <Input value={j.employer} onChange={e => updJoint(idx, 'employer', e.target.value)} data-testid={`s1-joint-${idx}-employer`} />
              </Field>
              <Field label="Income Type">
                <Select value={j.income_type} onValueChange={v => updJoint(idx, 'income_type', v)}>
                  <SelectTrigger data-testid={`s1-joint-${idx}-income-type`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salaried_my">Salaried (Malaysia)</SelectItem>
                    <SelectItem value="salaried_sg">Salaried (Singapore)</SelectItem>
                    <SelectItem value="self_employed">Self-Employed</SelectItem>
                    <SelectItem value="commission">Commission-Based</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────── Section 2 ───────────
function Section2Form({ data, setData }) {
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div className="space-y-4" data-testid="loan-portal-section-2">
      <h2 className="text-lg font-bold text-gray-800">Income & Loan Preferences</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Basic Salary (RM/month)" required>
          <Input type="number" value={data.basic_salary} onChange={e => upd('basic_salary', parseFloat(e.target.value) || 0)} data-testid="s2-basic" />
        </Field>
        <Field label="OT/Bonus avg (RM/month)">
          <Input type="number" value={data.ot_bonus_avg} onChange={e => upd('ot_bonus_avg', parseFloat(e.target.value) || 0)} data-testid="s2-ot" />
        </Field>
        <Field label="Commission avg (RM/month)">
          <Input type="number" value={data.commission_avg} onChange={e => upd('commission_avg', parseFloat(e.target.value) || 0)} data-testid="s2-comm" />
        </Field>
        <Field label="Singapore-sourced income?">
          <Select value={data.has_sg_income ? 'yes' : 'no'} onValueChange={v => upd('has_sg_income', v === 'yes')}>
            <SelectTrigger data-testid="s2-has-sg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {data.has_sg_income && (
          <Field label="SG Income (SGD/month)">
            <Input type="number" value={data.sg_income_amount}
                   onChange={e => upd('sg_income_amount', parseFloat(e.target.value) || 0)}
                   data-testid="s2-sg-amt" />
          </Field>
        )}
        <Field label="Existing housing loans">
          <Select value={data.existing_housing_loans} onValueChange={v => upd('existing_housing_loans', v)}>
            <SelectTrigger data-testid="s2-existing"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="1">1 loan</SelectItem>
              <SelectItem value="2_plus">2 or more</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Other monthly commitments (RM)" hint="Credit cards, personal loans, hire-purchase">
          <Input type="number" value={data.other_commitments}
                 onChange={e => upd('other_commitments', parseFloat(e.target.value) || 0)} data-testid="s2-other" />
        </Field>
        <Field label="Target loan margin (%)">
          <Select value={String(data.target_loan_margin_percent)}
                  onValueChange={v => upd('target_loan_margin_percent', parseInt(v))}>
            <SelectTrigger data-testid="s2-margin"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[70, 75, 80, 85, 90, 95].map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Loan tenure (years)" required>
          <Select value={String(data.tenure_years || 30)}
                  onValueChange={v => upd('tenure_years', parseInt(v))}>
            <SelectTrigger data-testid="s2-tenure"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[15, 20, 25, 30, 35, 40].map(y => <SelectItem key={y} value={String(y)}>{y} years</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Indicative interest rate (%)" hint="Default 4.10 — banks will provide exact rates later">
          <Input value={data.interest_rate || '4.10'}
                 onChange={e => upd('interest_rate', e.target.value)}
                 placeholder="4.10" data-testid="s2-rate" />
        </Field>
        <Field label="Downpayment source">
          <Select value={data.downpayment_source} onValueChange={v => upd('downpayment_source', v)}>
            <SelectTrigger data-testid="s2-dp"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="epf">EPF</SelectItem>
              <SelectItem value="family">Family</SelectItem>
              <SelectItem value="combination">Combination</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Loan type">
          <Select value={data.loan_type} onValueChange={v => upd('loan_type', v)}>
            <SelectTrigger data-testid="s2-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="conventional">Conventional</SelectItem>
              <SelectItem value="islamic">Islamic</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Extra fields for AICon */}
      <div className="border-t pt-4 mt-2">
        <h3 className="text-sm font-bold text-gray-800 mb-3">Credit Profile</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="CCRIS Status" hint="Your self-declared credit health status">
            <Select value={data.ccris_status || 'Healthy'} onValueChange={v => upd('ccris_status', v)}>
              <SelectTrigger data-testid="s2-ccris"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Healthy">Healthy — no arrears</SelectItem>
                <SelectItem value="New or no record">New or no record</SelectItem>
                <SelectItem value="Minor Issues">Minor Issues</SelectItem>
                <SelectItem value="Has Problem">Has Problem</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Total savings (RM)" hint="Combined savings across all accounts">
            <Input type="number" value={data.saving_amount || 0}
                   onChange={e => upd('saving_amount', parseFloat(e.target.value) || 0)}
                   data-testid="s2-savings" />
          </Field>
          <Field label="Is your PTPTN status OK?" hint="No arrears / fully settled">
            <Select value={data.is_ptptn_ok === false ? 'no' : 'yes'}
                    onValueChange={v => upd('is_ptptn_ok', v === 'yes')}>
              <SelectTrigger data-testid="s2-ptptn"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes — no PTPTN issues</SelectItem>
                <SelectItem value="no">No — has PTPTN arrears</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
    </div>
  );
}

// ─────────── Section 4 — Supporting Documents ───────────
function Section4Form({ data, setData, token }) {
  return (
    <div className="space-y-4" data-testid="loan-portal-section-4">
      <h2 className="text-lg font-bold text-gray-800">Supporting Documents</h2>
      <p className="text-xs text-gray-500 -mt-2">
        Upload the documents banks need to evaluate your loan. Fields marked <span className="text-red-500 font-semibold">*</span> are required.
      </p>

      <DocSlot
        docType="nric"
        title="NRIC (front & back)"
        hint="Clear photos/scans of both sides"
        required
        docs={data.nric || []}
        setDocs={docs => setData(d => ({ ...d, nric: docs }))}
        token={token}
        testidPrefix="s4-nric"
      />
      <DocSlot
        docType="payslips"
        title="Payslips (latest 3 months)"
        hint="PDF or image, up to 15 MB each"
        required
        docs={data.payslips || []}
        setDocs={docs => setData(d => ({ ...d, payslips: docs }))}
        token={token}
        testidPrefix="s4-payslip"
      />
      <DocSlot
        docType="bank_statements"
        title="Bank Statements (latest 6 months)"
        hint="Consolidated PDFs preferred"
        docs={data.bank_statements || []}
        setDocs={docs => setData(d => ({ ...d, bank_statements: docs }))}
        token={token}
        testidPrefix="s4-bank"
      />
      <DocSlot
        docType="epf_statement"
        title="EPF Statement (latest)"
        hint="i-Akaun snapshot works"
        docs={data.epf_statement || []}
        setDocs={docs => setData(d => ({ ...d, epf_statement: docs }))}
        token={token}
        testidPrefix="s4-epf"
      />
      <DocSlot
        docType="other"
        title="Other Supporting Documents"
        hint="Optional — tax return, BE form, commission letter, etc."
        docs={data.other || []}
        setDocs={docs => setData(d => ({ ...d, other: docs }))}
        token={token}
        testidPrefix="s4-other"
      />
    </div>
  );
}

function DocSlot({ docType, title, hint, required, docs, setDocs, token, testidPrefix }) {
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const r = await axios.post(
          `${API}/loan-portal/documents/upload`,
          form,
          { params: { token, doc_type: docType }, headers: { 'Content-Type': 'multipart/form-data' } },
        );
        const d = r.data?.data;
        if (d?.url) uploaded.push({ url: d.url, name: d.name, uploaded_at: d.uploaded_at });
      }
      setDocs([...docs, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} file(s)`);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAt = (idx) => setDocs(docs.filter((_, i) => i !== idx));

  return (
    <div className={`border rounded-xl p-4 bg-gray-50/50 ${required && docs.length === 0 ? 'border-red-200 bg-red-50/30' : ''}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            {title}
            {required && <span className="text-red-500" title="Required">*</span>}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {hint}
            {required && docs.length === 0 && (
              <span className="text-red-600 font-medium ml-2">Required — please upload at least one file.</span>
            )}
          </p>
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            className="hidden"
            onChange={onPick}
            disabled={uploading}
            data-testid={`${testidPrefix}-input`}
          />
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
            uploading ? 'bg-gray-200 text-gray-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </span>
        </label>
      </div>

      {docs.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {docs.map((d, i) => (
            <div key={i} className="flex items-center justify-between bg-white border rounded-md px-3 py-1.5 text-xs"
                 data-testid={`${testidPrefix}-item-${i}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-700">{d.name}</span>
              </div>
              <button onClick={() => removeAt(i)} className="text-red-500 hover:text-red-700"
                      data-testid={`${testidPrefix}-remove-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────── Consent ───────────
function ConsentForm({ data, setData, nricFromSection1, customerSnapshot, propertySummary }) {
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  // V4.24.74 — Signature canvas ref + drawing state for the AICon
  // "Signed by" panel that appears when the customer ticks the Aicon
  // CTOS/CCRIS retrieval consent.
  const sigCanvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const [sigEmpty, setSigEmpty] = React.useState(!(data.aicon_signature_data_url));

  React.useEffect(() => {
    // Restore signature from state if the user is coming back to Step 4.
    const canvas = sigCanvasRef.current;
    if (canvas && data.aicon_signature_data_url) {
      const ctx = canvas.getContext('2d');
      const img = new window.Image();
      img.onload = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0); };
      img.src = data.aicon_signature_data_url;
      setSigEmpty(false);
    }
  }, [data.aicon_signature_data_url]);

  const canvasPoint = (e) => {
    const canvas = sigCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = sigCanvasRef.current.getContext('2d');
    const { x, y } = canvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1f2937';
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = sigCanvasRef.current.getContext('2d');
    const { x, y } = canvasPoint(e);
    ctx.lineTo(x, y); ctx.stroke();
    if (sigEmpty) setSigEmpty(false);
  };
  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = sigCanvasRef.current.toDataURL('image/png');
    upd('aicon_signature_data_url', dataUrl);
    // Capture the ACTUAL sign time — becomes Aicon `consent_at[<nric>]`.
    upd('aicon_signed_at', new Date().toISOString());
  };
  const clearSig = () => {
    const canvas = sigCanvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSigEmpty(true);
    upd('aicon_signature_data_url', '');
    upd('aicon_signed_at', '');
  };

  // V4.24.82 — Build the "To:" address block from property_summary
  // (developer + address_1 + city + postcode + state).
  const developerName = (propertySummary?.developer || 'SAM PROPERTIES DEVELOPMENT').toUpperCase();
  const addrParts = [
    propertySummary?.address_1,
    [propertySummary?.postcode, propertySummary?.city].filter(Boolean).join(' '),
    propertySummary?.state,
  ].filter(Boolean);
  const todayStr = React.useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }, []);
  // V4.24.82 — Six purposes are fixed and always shown as ticked
  // (matches the PDF template — the customer opts-in to ALL by
  // ticking the parent "Authorise Aicon CTOS/CCRIS Retrieval").
  const CTOS_PURPOSES = [
    'Opening of account',
    'Credit/Account monitoring',
    'Debt recovery',
    'Credit/Account evaluation',
    'Credit/Account review',
    'Legal documentation consequent to a contract or facility granted by you.',
  ];

  return (
    <div className="space-y-4" data-testid="loan-portal-section-3">
      <h2 className="text-lg font-bold text-gray-800">Consent & Submit</h2>
      <p className="text-sm text-gray-600">
        Please review each consent below. All four are required to run your loan profile.
      </p>
      <div className="space-y-3">
        <ConsentRow
          checked={data.agreed_pdpa}
          onChange={v => upd('agreed_pdpa', v)}
          title="Personal Data Protection Act (PDPA)"
          desc="I consent to SAM Rumah collecting, storing and processing the personal data I have provided for the purpose of loan profiling, in accordance with the PDPA 2010."
          testid="consent-pdpa"
        />
        <ConsentRow
          checked={data.agreed_aicon_ctos}
          onChange={v => upd('agreed_aicon_ctos', v)}
          title="Authorise Aicon CTOS/CCRIS Retrieval"
          desc="I authorise SAM Rumah's loan-profiling partner (Aicon) to retrieve my CTOS / CCRIS credit reports for the sole purpose of bank pre-qualification."
          testid="consent-aicon"
        />

        {/* V4.24.82 — CTOS Public Consent Authorisation panel. Renders
            inline when the customer ticks the AICon consent. Content
            matches the SAM PROPERTIES CTOS consent PDF template. Left
            column = live customer data (Section 1) + digital signature.
            The right-side "Signed By" block from the PDF is intentionally
            NOT rendered in the customer UI (customer signs left only). */}
        {data.agreed_aicon_ctos && (
          <div className="ml-0 sm:ml-4 mt-2 p-5 border-2 border-indigo-200 bg-white rounded-lg space-y-4"
               data-testid="aicon-signed-by-panel">
            <div className="text-center border-b pb-2">
              <p className="text-xs font-semibold text-gray-500 tracking-widest uppercase">CTOS Public</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">Consent Authorisation</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-semibold text-gray-500">Date</p>
                <p className="font-medium text-gray-900" data-testid="ctos-date">{todayStr}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500">To</p>
                <p className="font-medium text-gray-900" data-testid="ctos-to-name">{developerName}</p>
                {addrParts.length > 0 && (
                  <p className="text-xs text-gray-700 leading-snug mt-0.5" data-testid="ctos-to-address">
                    {addrParts.join(', ')}
                  </p>
                )}
              </div>
            </div>

            <div className="text-xs text-gray-800 leading-relaxed space-y-2 border-t pt-3">
              <p>Dear Sir,</p>
              <p>
                Pursuant to the Credit Reporting Agencies (&quot;CRA&quot;) Act 2010, Central Bank of Malaysia Act 2009 and
                Personal Data Protection (&quot;PDP&quot;) Act 2010, I/we the undersigned do hereby give my/our consent to
                you and CTOS Data Systems Sdn Bhd (&quot;CTOS&quot;), a registered credit reporting agency under the CRA
                Act to process my/our company personal data.
              </p>
              <p className="font-semibold">By this consent, I/we understand and agree that:</p>
              <p>
                i) You may conduct credit/trade check including CCRIS checks on me/us and when consent has been given
                individually, on our directors, shareholders, guarantors, etc. with CTOS at any time for as long as I/we
                have a trade relationship with you or where any dues remain unpaid and outstanding with you, for any one
                or more of the following purposes:
              </p>
              <ul className="pl-5 space-y-0.5" data-testid="ctos-purposes">
                {CTOS_PURPOSES.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <p>
                ii) You may process and/or disclose my/our personal data and/or any information on my/our conduct of
                account(s) with you, to any business entity/ies for the purpose of trade reference submission. I/We
                am/are also aware and understand that such information will be provided to CTOS, who may in turn share
                such information to subscribers of their service.
              </p>
              <p>
                iii) Where you require any processing of my/our application to be processed by any processing centre
                located outside Malaysia (including your Head Office), I/we hereby give consent to CTOS to disclose
                my/our credit information except CCRIS, to such locations outside Malaysia.
              </p>
            </div>

            <div className="border-t pt-3 space-y-3">
              <h4 className="font-semibold text-indigo-900 text-sm">Signed By</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <Input value={customerSnapshot?.name || ''} readOnly className="bg-gray-50 mt-1"
                         data-testid="aicon-signer-name" />
                </div>
                <div>
                  <Label className="text-gray-600">IC Number</Label>
                  <Input value={customerSnapshot?.ic_number || ''} readOnly className="bg-gray-50 mt-1"
                         data-testid="aicon-signer-ic" />
                </div>
                <div>
                  <Label className="text-gray-600">Designation</Label>
                  <Input value={customerSnapshot?.designation || 'Individual'} readOnly className="bg-gray-50 mt-1"
                         data-testid="aicon-signer-designation" />
                </div>
                <div>
                  <Label className="text-gray-600">Mobile Number</Label>
                  <Input value={customerSnapshot?.mobile || ''} readOnly className="bg-gray-50 mt-1"
                         data-testid="aicon-signer-mobile" />
                </div>
              </div>

              <div>
                <Label className="text-gray-600 text-xs">Digital Signature</Label>
                <div className="mt-1 border-2 border-dashed border-gray-300 rounded bg-white">
                  <canvas
                    ref={sigCanvasRef}
                    width={520}
                    height={140}
                    className="w-full touch-none rounded"
                    onMouseDown={startDraw} onMouseMove={draw}
                    onMouseUp={endDraw} onMouseLeave={endDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                    data-testid="aicon-signature-canvas"
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xs ${sigEmpty ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {sigEmpty ? 'Signature required — sign here to authorise Aicon retrieval' : 'Signature captured ✓'}
                  </p>
                  <button type="button" onClick={clearSig}
                          className="text-xs text-red-600 hover:text-red-800"
                          data-testid="aicon-signature-clear">
                    Clear
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[10px] text-gray-500 border-t pt-2 italic">
              For more information about the CRA Act, please visit the website of Registrar Office of Credit Reporting
              Agencies (https://ppapk.treasury.gov.my), contact them via email (aduan.ppapk@gov.my) or phone
              (03-8890 6559 / 03-8890 6560).
            </p>
          </div>
        )}

        <ConsentRow
          checked={data.agreed_bank_fanout}
          onChange={v => upd('agreed_bank_fanout', v)}
          title="Forward to Participating Banks"
          desc="I consent to SAM Rumah forwarding my loan profile to up to 3 shortlisted participating banks for indicative offer evaluation."
          testid="consent-fanout"
        />
        <ConsentRow
          checked={data.agreed_outsource_fallback}
          onChange={v => upd('agreed_outsource_fallback', v)}
          title="Outsource Broker Fallback"
          desc="If all direct participating banks decline, I authorise SAM Rumah to engage an outsource mortgage broker on my behalf."
          testid="consent-outsource"
        />
      </div>

      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <Label className="text-sm font-medium text-amber-800">Type your NRIC again to sign</Label>
        <Input
          value={data.typed_nric}
          onChange={e => upd('typed_nric', e.target.value)}
          placeholder={nricFromSection1 || 'NRIC from Section 1'}
          className="mt-2 bg-white"
          data-testid="consent-typed-nric"
        />
        <p className="text-xs text-amber-700 mt-1">
          This must match the NRIC you provided in Section 1.
        </p>
      </div>
    </div>
  );
}

// ─────────── Result Panel ───────────
function ResultPanel({ caseData }) {
  const verdict = caseData?.assessment_verdict;
  const band = verdict?.risk_band || 'pending';
  const bandStyle = {
    green: { bg: 'bg-emerald-50', text: 'text-emerald-800', icon: <CheckCircle2 className="w-8 h-8 text-emerald-600" />, label: 'Approved Profile' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-800', icon: <AlertTriangle className="w-8 h-8 text-amber-600" />, label: 'Borderline Profile' },
    red: { bg: 'bg-red-50', text: 'text-red-800', icon: <AlertTriangle className="w-8 h-8 text-red-600" />, label: 'Special Handling' },
  }[band] || { bg: 'bg-gray-50', text: 'text-gray-700', icon: null, label: 'Profile Pending' };

  return (
    <div className="space-y-4" data-testid="loan-portal-result">
      <div className={`rounded-2xl border p-6 ${bandStyle.bg}`}>
        <div className="flex items-start gap-4">
          {bandStyle.icon}
          <div className="flex-1">
            <h2 className={`text-xl font-bold ${bandStyle.text}`}>{bandStyle.label}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Your loan profile has been submitted. {verdict?.headline_reasons?.length > 0 && verdict.headline_reasons.join(' · ')}
            </p>
          </div>
        </div>
        {verdict?.recommended_loan_amount > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-xs text-gray-600 uppercase">Qualified Income</p>
              <p className="text-lg font-bold">RM {Number(verdict.qualified_income).toLocaleString()}</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-xs text-gray-600 uppercase">DSR</p>
              <p className="text-lg font-bold">{verdict.overall_dsr_percent}%</p>
            </div>
            <div className="bg-white/70 rounded-lg p-3 col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-600 uppercase">Recommended Loan</p>
              <p className="text-lg font-bold text-indigo-700">RM {Number(verdict.recommended_loan_amount).toLocaleString()}</p>
            </div>
          </div>
        )}
      </div>

      {verdict?.top_banks?.length > 0 && (
        <div className="bg-white border rounded-2xl p-5">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-indigo-600" /> Top Bank Matches
          </h3>
          <div className="space-y-2">
            {verdict.top_banks.map((b, i) => (
              <div key={i} className="flex items-center justify-between border-b last:border-b-0 py-2"
                   data-testid={`result-bank-${i}`}>
                <div>
                  <p className="font-semibold text-gray-800">{b.bank}</p>
                  <p className="text-xs text-gray-500">
                    {b.reasons?.join(' · ')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-700">RM {Number(b.max_loan).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">~{b.estimated_rate}% · {Math.round(b.probability * 100)}% match</p>
                </div>
              </div>
            ))}
          </div>
          {/* V4.24.14 — Regulatory disclaimer required alongside the recommended banks list. */}
          <p
            className="text-xs text-gray-500 italic mt-3 leading-relaxed"
            data-testid="recommended-banks-disclaimer"
          >
            The banks listed are recommendations only. Other banks not listed may
            also provide home loan offers, subject to their assessment and approval.
          </p>
        </div>
      )}

      {verdict?.hard_blockers?.length > 0 && (
        <div className="bg-white border border-red-200 rounded-2xl p-5">
          <h3 className="font-bold text-red-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" /> Things to Address
          </h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {verdict.hard_blockers.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            Your SAM Rumah agent will engage an outsource broker on your behalf and reach out shortly.
          </p>
        </div>
      )}

      <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 text-sm text-indigo-900">
        <p className="font-semibold mb-1">What happens next?</p>
        <p>Your SAM Rumah agent will review the result and forward your profile to the shortlisted banks. You&apos;ll receive offers in the customer app (Loan Offers tab) within 1–3 business days.</p>
      </div>
    </div>
  );
}

// ─────────── Small helpers ───────────
function Field({ label, required, hint, locked, children }) {
  return (
    <div>
      <Label className="text-xs text-gray-600 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {locked && <Lock className="w-3 h-3 text-gray-400" title="Prefilled — cannot be edited" />}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function ConsentRow({ checked, onChange, title, desc, testid }) {
  return (
    <label className={`flex gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
      checked ? 'bg-emerald-50 border-emerald-300' : 'bg-white hover:bg-gray-50'
    }`}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-1" data-testid={testid} />
      <div className="flex-1">
        <p className="font-semibold text-gray-800 text-sm">{title}</p>
        <p className="text-xs text-gray-600 mt-1">{desc}</p>
      </div>
    </label>
  );
}

function FullPageStatus({ icon, title, message, hint }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 via-white to-white px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border p-8 text-center">
        <div className="flex justify-center mb-3">{icon}</div>
        <h1 className="text-xl font-bold text-gray-800">{title}</h1>
        {message && <p className="text-sm text-gray-600 mt-2">{message}</p>}
        {hint && <p className="text-xs text-gray-500 mt-3">{hint}</p>}
      </div>
    </div>
  );
}

function handleValidationError(e, fallback) {
  const r = e?.response?.data;
  if (Array.isArray(r?.errors) && r.errors.length > 0) {
    toast.error(`${fallback}: ${r.errors.map(x => `${x.field} — ${x.message}`).join('; ')}`);
  } else {
    toast.error(r?.message || r?.detail || fallback);
  }
}

function emptySection1() {
  return {
    // Prefill-eligible (booking-sourced)
    full_name: '', email: '', phone: '', residential_address: '',
    // Existing questionnaire fields
    nric: '', date_of_birth: '', nationality: 'Malaysian', marital_status: '',
    dependents: 0, employer: '', job_title: '', months_in_current_job: 0,
    on_probation: false, income_type: '',
    // Joint applicants
    joint_applicants: [],
  };
}

function emptySection2() {
  return {
    basic_salary: 0, ot_bonus_avg: 0, commission_avg: 0,
    has_sg_income: false, sg_income_amount: 0,
    existing_housing_loans: 'none', other_commitments: 0,
    target_loan_margin_percent: 90, downpayment_source: 'cash',
    loan_type: 'conventional',
    // V4.24.2 additions
    tenure_years: 30,
    interest_rate: '4.10',
    ccris_status: 'Healthy',
    saving_amount: 0,
    is_ptptn_ok: true,
  };
}

function emptySection4() {
  return {
    nric: [], payslips: [], bank_statements: [], epf_statement: [], other: [],
  };
}

function emptyJoint() {
  return {
    full_name: '', nric: '', relation: 'spouse',
    date_of_birth: '', nationality: 'Malaysian', marital_status: 'married',
    dependents: 0, employer: '', job_title: '', months_in_current_job: 0,
    on_probation: false, income_type: 'salaried_my',
    basic_salary: 0, ot_bonus_avg: 0, commission_avg: 0, other_commitments: 0,
  };
}
